"use client";

import { useState, useEffect, useCallback } from "react";
import { useSession } from "next-auth/react";
import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Search, UserPlus, MessageSquare, Users } from "lucide-react";
import { searchUsers, getFavoriteContactIds } from "@/app/actions/messaging";
import { createOrGetConversation } from "@/app/actions/messaging";
import { toast } from "sonner";
import { FavoriteContactsList } from "./favorite-contacts-list";
import { UserProfilePopover } from "./user-profile-popover";

interface User {
  id: string;
  name: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  image: string | null;
  role: string;
  profile?: {
    company: string | null;
    role: string | null;
    bio: string | null;
  } | null;
}

interface ContactsListProps {
  onStartConversation?: (conversationId: string) => void;
}

export function ContactsList({ onStartConversation }: ContactsListProps) {
  const { data: session } = useSession();
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [startingConversation, setStartingConversation] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [favoriteContactIds, setFavoriteContactIds] = useState<string[]>([]);
  const [favoritesRefreshKey, setFavoritesRefreshKey] = useState(0);

  const isAdmin = session?.user?.role === "ADMIN";

  // Fetch favorite contact IDs
  useEffect(() => {
    async function fetchFavorites() {
      const result = await getFavoriteContactIds();
      if (!result.error) {
        setFavoriteContactIds(result.favoriteIds);
      }
    }
    fetchFavorites();
  }, [favoritesRefreshKey]);

  const handleFavoriteChange = useCallback(() => {
    // Trigger a refresh of favorites
    setFavoritesRefreshKey((prev) => prev + 1);
  }, []);

  // Load all users for admins on mount
  useEffect(() => {
    if (isAdmin && searchQuery.trim().length === 0) {
      loadAllUsersForAdmin();
    }
  }, [isAdmin]);

  const loadAllUsersForAdmin = async () => {
    setLoading(true);
    try {
      const result = await searchUsers(" ", {
        searchByEmail: false,
        searchByRole: false,
        filterRecent: false,
        limit: 100,
      });

      if (result.error) {
        toast.error(result.error);
      } else {
        setUsers(result.users);
        setHasSearched(true);
      }
    } catch (error) {
      console.error("Load users error:", error);
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  // Search users when query changes (with debounce)
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setUsers([]);
      setHasSearched(false);
      // For admins, reload all users when search is cleared
      if (isAdmin) {
        loadAllUsersForAdmin();
      }
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const result = await searchUsers(searchQuery, {
          searchByEmail: true,
          searchByRole: true,
          filterRecent: false,
          limit: isAdmin ? 100 : 20,
        });

        if (result.error) {
          toast.error(result.error);
        } else {
          setUsers(result.users);
          setHasSearched(true);
        }
      } catch (error) {
        console.error("Search users error:", error);
        toast.error("Failed to search users");
      } finally {
        setLoading(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, isAdmin]);

  const handleStartConversation = async (userId: string) => {
    try {
      setStartingConversation(userId);

      const result = await createOrGetConversation(userId);

      if (result.error) {
        toast.error(result.error);
        return;
      }

      if (result.conversation) {
        toast.success("Conversation started");
        onStartConversation?.(result.conversation.id);
      }
    } catch (error) {
      console.error("Start conversation error:", error);
      toast.error("Failed to start conversation");
    } finally {
      setStartingConversation(null);
    }
  };

  const getDisplayName = (user: User) => {
    if (user.name) return user.name;
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user.firstName) return user.firstName;
    return user.email?.split("@")[0] || "Unknown User";
  };

  const getInitials = (user: User) => {
    const name = getDisplayName(user);
    const parts = name.split(" ");
    if (parts.length >= 2 && parts[0] && parts[1]) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Favorite Contacts Section */}
      <FavoriteContactsList
        key={favoritesRefreshKey}
        onStartConversation={onStartConversation}
        onContactRemoved={handleFavoriteChange}
      />

      {/* Search Bar */}
      <div className="p-4 border-b border-cyan-500/20 bg-gray-900/60 backdrop-blur-sm">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            id="contact-search"
            type="search"
            placeholder="Search by name, email, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={cn(
              "pl-9 bg-gray-800/60 border-border/15",
              "focus:border-cyan-500/50 focus:ring-2 focus:ring-cyan-500/20",
              "placeholder:text-gray-500 text-gray-200",
              "transition-[border-color,box-shadow] duration-200"
            )}
          />
        </div>
        <p className="text-xs text-gray-500 mt-2">
          Type at least 2 characters to search
        </p>
      </div>

      {/* Users List */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-2">
          {loading ? (
            <>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="p-3 animate-pulse">
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-12 w-12 rounded-full bg-gray-800/50" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-32 bg-gray-800/50" />
                      <Skeleton className="h-3 w-full bg-gray-800/50" />
                    </div>
                  </div>
                </div>
              ))}
            </>
          ) : !hasSearched ? (
            <div className="text-center py-16">
              <div className="relative inline-block">
                <Users className="mx-auto h-14 w-14 text-gray-600 opacity-50" />
                <div className="absolute inset-0 h-14 w-14 mx-auto rounded-full bg-cyan-500/5 animate-ping" />
              </div>
              <h3 className="mt-6 text-sm font-semibold text-gray-400">
                Find People to Message
              </h3>
              <p className="mt-2 text-xs text-gray-500 max-w-xs mx-auto leading-relaxed">
                Search for users by name, email, or role to start a conversation
              </p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-16">
              <div className="relative inline-block">
                <Search className="mx-auto h-14 w-14 text-gray-600 opacity-50" />
                <div className="absolute inset-0 h-14 w-14 mx-auto rounded-full bg-cyan-500/5" />
              </div>
              <h3 className="mt-6 text-sm font-semibold text-gray-400">
                No users found
              </h3>
              <p className="mt-2 text-xs text-gray-500 max-w-xs mx-auto">
                Try a different search term
              </p>
            </div>
          ) : (
            users.map((user, index) => (
              <div
                key={user.id}
                className={cn(
                  "group relative",
                  "rounded-lg p-3",
                  "bg-gray-800/40 hover:bg-gray-800/60",
                  "border border-border/5 hover:border-cyan-500/30",
                  "transition-all duration-200",
                  "cursor-pointer",
                  "animate-fade-in"
                )}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="flex items-center gap-3">
                  {/* Avatar with Profile Popover */}
                  <UserProfilePopover
                    user={{
                      id: user.id,
                      name: user.name,
                      email: user.email,
                      image: user.image,
                    }}
                    isFavorite={favoriteContactIds.includes(user.id)}
                    onFavoriteChange={handleFavoriteChange}
                    onStartConversation={onStartConversation}
                    side="right"
                    align="start"
                  >
                    <Avatar className="h-12 w-12 ring-2 ring-white/10 hover:ring-cyan-500/50 transition-all">
                      <AvatarImage
                        src={user.image || undefined}
                        alt={getDisplayName(user)}
                      />
                      <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-500 text-white text-sm font-semibold">
                        {getInitials(user)}
                      </AvatarFallback>
                    </Avatar>
                  </UserProfilePopover>

                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="text-sm font-semibold text-white truncate">
                        {getDisplayName(user)}
                      </h4>
                      {user.role === "ADMIN" && (
                        <Badge variant="outline" className="text-xs border-cyan-500/50 text-cyan-400">
                          Admin
                        </Badge>
                      )}
                    </div>

                    {user.email && (
                      <p className="text-xs text-gray-400 truncate">
                        {user.email}
                      </p>
                    )}

                    {user.profile?.company && (
                      <p className="text-xs text-gray-500 truncate mt-1">
                        {user.profile.role ? `${user.profile.role} at ` : ""}
                        {user.profile.company}
                      </p>
                    )}
                  </div>

                  {/* Action Button */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleStartConversation(user.id)}
                    disabled={startingConversation === user.id}
                    className={cn(
                      "shrink-0",
                      "h-9 px-3",
                      "bg-cyan-500/10 hover:bg-cyan-500/20",
                      "text-cyan-400 hover:text-cyan-300",
                      "border border-cyan-500/30 hover:border-cyan-500/50",
                      "transition-all duration-200"
                    )}
                  >
                    {startingConversation === user.id ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Starting...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="h-4 w-4 mr-2" />
                        Message
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
